import { h } from "@/lib/dom";
import {
  openModal,
  field,
  textInput,
  textArea,
  select,
  button,
  toast,
} from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { navigate } from "@/app/router";
import type { Topic, TopicState } from "@/types/models";

const STATE_OPTIONS = [
  { value: "active", label: "Aktiv" },
  { value: "done", label: "Abgeschlossen" },
];

export function openTopicForm(opts: {
  subjectId: string;
  topic?: Topic;
  onSaved: () => void;
}): void {
  const editing = !!opts.topic;
  const titleInput = textInput({
    value: opts.topic?.title ?? "",
    placeholder: "z. B. Analysis – Ableitungen",
    required: true,
  });
  const descInput = textArea({
    value: opts.topic?.description ?? "",
    placeholder: "Kurze Beschreibung (optional)",
    rows: 2,
  });
  const stateSelect = select({
    options: STATE_OPTIONS,
    value: opts.topic?.state ?? "active",
  });
  const errorBox = h("div", { class: "field-error" });
  const saveBtn = button({
    label: editing ? "Speichern" : "Anlegen",
    variant: "primary",
    onClick: save,
  });

  async function save() {
    const title = titleInput.value.trim();
    if (!title) {
      errorBox.textContent = "Bitte einen Titel eingeben.";
      return;
    }
    saveBtn.disabled = true;
    errorBox.textContent = "";
    try {
      if (editing && opts.topic) {
        // Beim Bearbeiten: Beschreibung + Status weiterhin pflegbar.
        await topicsService.update(opts.topic.id, {
          title,
          description: descInput.value.trim(),
          state: stateSelect.value as TopicState,
        });
        toast("Thema aktualisiert.", "success");
        handle.close();
        opts.onSaved();
      } else {
        // Beim Anlegen: kein Status/keine Beschreibung – Status ergibt sich
        // automatisch aus dem Prüfungstermin (computeAutoState). Termine
        // selbst kommen danach über die Thema-Einstellungen dazu.
        const created = await topicsService.create({
          subject: opts.subjectId,
          title,
        });
        handle.close();
        opts.onSaved();
        // Direkt ins neue Thema springen – wer ein Thema anlegt, will
        // meistens sofort dort weiterarbeiten.
        navigate(`/topic/${created.id}`);
      }
    } catch (err) {
      errorBox.textContent = pbErrorMessage(err);
      saveBtn.disabled = false;
    }
  }

  titleInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void save();
    }
  });

  const handle = openModal({
    title: editing ? "Thema bearbeiten" : "Neues Thema",
    // Öffnet direkt mittig statt als Bottom-Sheet: der Titel bekommt sofort
    // den Fokus (siehe unten), die Tastatur klappt also praktisch augenblicklich
    // auf – ein Sheet, das erst unten startet, rutscht dann sichtbar noch
    // einmal nach oben.
    centered: true,
    body: h(
      "div",
      null,
      field("Titel", titleInput),
      // Beschreibung & Status nur beim Bearbeiten – beim Anlegen unnötig.
      editing ? field("Beschreibung", descInput) : null,
      editing ? field("Status", stateSelect) : null,
      errorBox
    ),
    footer: h(
      "div",
      { class: "row" },
      button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
      saveBtn
    ),
  });

  setTimeout(() => titleInput.focus(), 50);
}
